import { Helmet } from "react-helmet-async";
import Screen from "../Layouts/Screen/Screen";
import "./style.css"
import ItemList from "../../components/ItemList/ItemList";
import { Archive, Award, Bell, ChevronRight, DollarSign, Eye, EyeOff, FileText, MapPin, MessageCircle, Star, Tag, UserMinus, Users } from "react-feather";
import { Accessibility, Bubbles, CheckCheck, PersonStanding } from "lucide-react";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { ViewProfilePrivacy } from "../../global-components/src/types";
import { useAuth, useMessage, useTheme } from "../../global-components/src";
import { errorMessage } from "../../global-components/src/utils/text";
import { api } from "../../services/api";
 
type MessageRequestPermission = 'allow' | 'requests' | 'off';
type MessageSettings = {
    following: MessageRequestPermission;
    followers: MessageRequestPermission;
    others: MessageRequestPermission;
    inbox_categories_enabled: boolean;
};

const defaultMessageSettings: MessageSettings = {
    following: 'allow',
    followers: 'requests',
    others: 'requests',
    inbox_categories_enabled: false,
};

const Privacy = () => {
    const { token, user, setUser } = useAuth();
    const [viewOnline, setViewOnline] = useState<ViewProfilePrivacy>("all");
    const [loading, setLoading] = useState(false);
    const [showCountry, setShowCountry] = useState(true);
    const [loadingCountry, setLoadingCountry] = useState(false);
    const [readReceiptsEnabled, setReadReceiptsEnabled] = useState(true);
    const [loadingReadReceipts, setLoadingReadReceipts] = useState(false);
    const [messageSettings, setMessageSettings] = useState<MessageSettings>(defaultMessageSettings);
    const [loadingMessageSettings, setLoadingMessageSettings] = useState(false);
    const { isDarkMode } = useTheme();
    const { setMessage } = useMessage();

    useEffect(() => {
        if(user?.show_online) setViewOnline(user?.show_online);
    }, [user?.show_online])

    useEffect(() => {
        setShowCountry(user?.show_country !== false);
    }, [user?.show_country])

    useEffect(() => {
        setReadReceiptsEnabled(user?.read_receipts_enabled !== false);
    }, [user?.read_receipts_enabled])

    useEffect(() => {
        if (!token) return;
        api(token).get('/user/message-settings')
            .then(response => setMessageSettings({ ...defaultMessageSettings, ...response.data }))
            .catch(() => undefined);
    }, [token]);

    const updateMessageSettings = async (patch: Partial<MessageSettings>) => {
        const previous = messageSettings;
        const next = { ...messageSettings, ...patch };
        setMessageSettings(next);
        try {
            setLoadingMessageSettings(true);
            const response = await api(token).put('/user/message-settings', patch);
            setMessageSettings({ ...defaultMessageSettings, ...response.data });
            setMessage(t('settings.privacy.messageRequests.saved'), 'success');
        } catch (err) {
            setMessageSettings(previous);
            setMessage(errorMessage(err), 'error');
        } finally {
            setLoadingMessageSettings(false);
        }
    };

    const messagePermissionLabel = (value: MessageRequestPermission) => t(`settings.privacy.messageRequests.${value}`);

    const handleChangeReadReceipts = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.checked;
        const previous = readReceiptsEnabled;
        setReadReceiptsEnabled(value);
        setUser(current => current ? { ...current, read_receipts_enabled: value } : current);

        try {
            setLoadingReadReceipts(true);
            await api(token).put('/user/read-receipts', { read_receipts_enabled: value });
            setMessage(
                t('settings.privacy.readReceipts.successChange', { defaultValue: 'Confirmações de leitura alteradas!' }),
                'success'
            );
        } catch (err) {
            setReadReceiptsEnabled(previous);
            setUser(current => current ? { ...current, read_receipts_enabled: previous } : current);
            setMessage(errorMessage(err), 'error');
        } finally {
            setLoadingReadReceipts(false);
        }
    };

    const handleChangeShowCountry = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.checked;
        const previous = showCountry;
        setShowCountry(val);
        try {
            setLoadingCountry(true);
            await api(token).put("/user/show-country", { show_country: val });
            setMessage(t("settings.privacy.showCountry.successChange", { defaultValue: "Visibilidade do país alterada!" }), "success");
        } catch (err) {
            setShowCountry(previous);
            setMessage(errorMessage(err), "error");
            console.error(err);
        } finally {
            setLoadingCountry(false);
        }
    };

    const handleChangeViewOnline = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        updateViewOnline(val as ViewProfilePrivacy);
        setViewOnline(val as ViewProfilePrivacy);
    };
    
    const updateViewOnline = async(viewOnline: ViewProfilePrivacy) => {
        try {
            setLoading(true);
            await api(token).put("/user/show-online", { show_online: viewOnline })
            setMessage(t("settings.privacy.viewOnline.successChange"), "success")
        } catch (err) {
            const messageError = errorMessage(err);
            setMessage(messageError, "error")
            console.error(err);
            console.error(messageError);
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <Screen paddingTopTitle={true} title={t("settings.privacy.title")}>
                <Helmet>
                        <title>Paçoca - {t("settings.privacy.title")}</title>
                        <meta name="description" content={`Paçoca - Privacidade, configure notificações, privacidade, sessões ativas doação, termos de uso, diretrizes, significado dos selos!`}/>
                        <meta name="keywords" content={`Paçoca, rede social brasileira, Privacidade, privacidade, termos de uso, doação, diretrizes, conexões, notificações, digitais}`}/>
                        <meta name="author" content="Paçoca Inc."/>
                        <meta property="og:title" content={`Paçoca - Privacidade - Rede Social`}/>
                        <meta property="og:description" content={`Paçoca - Privacidade, configure notificações, privacidade, sessões ativas doação, termos de uso, diretrizes, significado dos selos momentos!`}/>
                        <meta property="og:image" content="/img/link-preview.png?v=1" />
                        <meta property="og:image:width" content="1200" />
                        <meta property="og:image:height" content="630" />
                        <meta property="og:url" content={window.location.href}/>
                        <meta property="og:type" content="website"/>
                        <meta name="twitter:card" content="summary_large_image"/>
                        <meta name="twitter:title" content={`Paçoca - Privacidade - Rede Social`}/>
                        <meta name="twitter:description" content={`Paçoca - Privacidade, configure notificações, privacidade, sessões ativas doação, termos de uso, diretrizes, significado dos selos momentos!`}/>
                        <meta name="twitter:image" content="/img/link-preview.png?v=1" />
                </Helmet>

                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/privacidade/visibilidade-perfil">
                    <Eye style={{ marginRight: 10 }} className="icon-settings"/> {t("settings.privacy.privacyProfile")}
                    <ChevronRight className="chevron-right"/>
                </ItemList>

                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/privacidade/bloqueados">
                    <UserMinus style={{ marginRight: 10 }} className="icon-settings"/> {t("settings.privacy.userBlocks")}
                    <ChevronRight className="chevron-right"/>
                </ItemList>

                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/privacidade/ocultos">
                    <EyeOff style={{ marginRight: 10 }} className="icon-settings"/> Usuários ocultados
                    <ChevronRight className="chevron-right"/>
                </ItemList>

                <ItemList className="user-select-none" bigPaddingHorizontalNotification link="/privacidade/conteudo">
                    <Tag style={{ marginRight: 10 }} className="icon-settings"/> {t("settings.contentPreferences.title")}
                    <ChevronRight className="chevron-right"/>
                </ItemList>

                <ItemList className="user-select-none" bigPaddingHorizontalNotification>
                    <MapPin style={{ marginRight: 10 }} className="icon-settings"/> {t("settings.privacy.showCountry.title", { defaultValue: "Mostrar meu país no perfil público" })}

                    <div className="col form-check form-switch" style={{ paddingLeft: 20, display: "flex", justifyContent: "flex-end" }}>
                        <input
                            type="checkbox"
                            role="switch"
                            className="form-check-input"
                            disabled={loadingCountry}
                            checked={showCountry}
                            onChange={handleChangeShowCountry}
                            style={{ cursor: "pointer" }}
                        />
                    </div>
                </ItemList>


                <ItemList className="user-select-none" bigPaddingHorizontalNotification>
                    <MessageCircle style={{ marginRight: 10 }} className="icon-settings"/>
                    <div style={{ flex: 1 }}>
                        <strong>{t('settings.privacy.messageRequests.title')}</strong>
                        <small style={{ display: 'block', opacity: .7 }}>{t('settings.privacy.messageRequests.description')}</small>
                    </div>
                </ItemList>

                {([
                    ['following', t('settings.privacy.messageRequests.following')],
                    ['followers', t('settings.privacy.messageRequests.followers')],
                    ['others', t('settings.privacy.messageRequests.others')],
                ] as Array<[keyof Pick<MessageSettings, 'following' | 'followers' | 'others'>, string]>).map(([key, label]) => (
                    <ItemList key={key} className="user-select-none" bigPaddingHorizontalNotification>
                        <span style={{ flex: 1 }}>{label}</span>
                        <select
                            disabled={loadingMessageSettings}
                            className="form-control language"
                            value={messageSettings[key]}
                            onChange={event => updateMessageSettings({ [key]: event.target.value as MessageRequestPermission })}
                            style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: isDarkMode ? '#fff' : '#000', fontSize: 14, cursor: 'pointer' }}
                            aria-label={t('settings.privacy.messageRequests.permissionFor', { category: label })}
                        >
                            {(['allow', 'requests', 'off'] as MessageRequestPermission[]).map(value => (
                                <option key={value} value={value}>{messagePermissionLabel(value)}</option>
                            ))}
                        </select>
                    </ItemList>
                ))}

                <ItemList className="user-select-none" bigPaddingHorizontalNotification>
                    <div style={{ flex: 1 }}>
                        <span>{t('settings.privacy.messageRequests.inboxCategories')}</span>
                        <small style={{ display: 'block', opacity: .7 }}>{t('settings.privacy.messageRequests.inboxCategoriesDescription')}</small>
                    </div>
                    <div className="col form-check form-switch" style={{ paddingLeft: 20, display: 'flex', justifyContent: 'flex-end' }}>
                        <input
                            type="checkbox"
                            role="switch"
                            className="form-check-input"
                            disabled={loadingMessageSettings}
                            checked={messageSettings.inbox_categories_enabled}
                            onChange={event => updateMessageSettings({ inbox_categories_enabled: event.target.checked })}
                            style={{ cursor: 'pointer' }}
                        />
                    </div>
                </ItemList>

                <ItemList className="user-select-none" bigPaddingHorizontalNotification>
                    <CheckCheck style={{ marginRight: 10 }} className="icon-settings"/> {t('settings.privacy.readReceipts.title', { defaultValue: 'Confirmações de leitura' })}

                    <div className="col form-check form-switch" style={{ paddingLeft: 20, display: "flex", justifyContent: "flex-end" }}>
                        <input
                            type="checkbox"
                            role="switch"
                            className="form-check-input"
                            disabled={loadingReadReceipts}
                            checked={readReceiptsEnabled}
                            onChange={handleChangeReadReceipts}
                            style={{ cursor: "pointer" }}
                        />
                    </div>
                </ItemList>

                <ItemList className="user-select-none" bigPaddingHorizontalNotification>
                    <Bubbles style={{ marginRight: 10 }} className="icon-settings"/> {t("settings.privacy.viewOnline.title")}

                    <div className="col" style={{ paddingLeft: 20 }}>
                     <select
                        disabled={loading}
                        className="form-control language"
                        value={viewOnline}
                        onChange={handleChangeViewOnline}
                        style={{
                            marginLeft: "auto",
                            background: "transparent",
                            border: "none",
                            color: isDarkMode ? "#fff" : "#000",
                            fontSize: 14,
                            cursor: "pointer"
                        }}
                        >
                            <option value="all">{t("settings.privacy.viewOnline.all")}</option>
                            <option value="following">{t("settings.privacy.viewOnline.following")}</option>
                            <option value="none">{t("settings.privacy.viewOnline.none")}</option>
                        </select>
                   </div>
                </ItemList>
            </Screen>
        </>
    );
};

export default Privacy;